import { Box, Paper, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useVoIP } from '../hooks/useVoIP';
import { UAState, CallState } from '../types';

export const VoIPDebugPanel = () => {
  const { t } = useTranslation();
  const { uaState, isRegistered, callState, currentCall, hasJsSipExtension, isMuted, isOnHold, showDebugPanel } = useVoIP();

  if (!showDebugPanel) {
    return null;
  }

  if (!hasJsSipExtension) {
    return (
      <Paper sx={{ 
        p: 1, 
        position: 'fixed', 
        bottom: 8, 
        left: '50%', 
        transform: 'translateX(-50%)',
        zIndex: 9999, 
        minWidth: 200,
        maxWidth: 250,
      }}>
        <Typography variant="caption" color="error">{t('voip.extension_not_configured')}</Typography>
      </Paper>
    );
  }

  const getUAStateColor = () => {
    switch (uaState) {
      case UAState.REGISTERED: return 'success';
      case UAState.CONNECTING: return 'warning';
      case UAState.CONNECTED: return 'info';
      case UAState.REGISTRATION_FAILED: return 'error';
      case UAState.UNREGISTERED: return 'warning';
      default: return 'default';
    }
  };

  const getCallStateColor = () => {
    switch (callState) {
      case CallState.CONNECTED: return 'success';
      case CallState.INCOMING: return 'warning';
      case CallState.OUTGOING: return 'info';
      case CallState.ENDED: return 'error';
      default: return 'default';
    }
  };

  return (
    <Paper sx={{ 
      p: 1, 
      position: 'fixed', 
      bottom: 8, 
      left: '50%', 
      transform: 'translateX(-50%)',
      zIndex: 9999, 
      minWidth: 200,
      maxWidth: 250,
    }}>
      <Typography variant="caption" fontWeight="bold" gutterBottom display="block">{t('voip.debug_title')}</Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{t('voip.debug_ua_label')}</Typography>
          <Chip label={uaState} color={getUAStateColor()} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{t('voip.debug_registered_label')}</Typography>
          <Chip label={isRegistered ? t('common.yes') : t('common.no')} color={isRegistered ? 'success' : 'error'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{t('voip.debug_call_label')}</Typography>
          <Chip label={callState} color={getCallStateColor()} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{t('voip.debug_muted_label')}</Typography>
          <Chip label={isMuted ? t('common.yes') : t('common.no')} color={isMuted ? 'warning' : 'default'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{t('voip.debug_hold_label')}</Typography>
          <Chip label={isOnHold ? t('common.yes') : t('common.no')} color={isOnHold ? 'warning' : 'default'} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        </Box>
        
        {currentCall && (
          <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" display="block" fontSize="0.65rem">📞 {currentCall.phoneNumber}</Typography>
            <Typography variant="caption" display="block" fontSize="0.6rem">{currentCall.direction}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
